import { QuestionType, type QuestionItemInput } from "@/lib/gql/__generated__/graphql";
import { getUUID } from "@/lib/uuid/get-uuid";

import type {
	ForArrayType,
	ForAutoGenerator,
	ForNodeType,
} from "@/components/features/_model/types/auto-generator-for-view-model";

type Props = {
	questions: ForArrayType<QuestionItemInput[]>;

	onSetViewModel: <K extends "questions">(values: { key: K; value: ForArrayType<QuestionItemInput[]> }[]) => void;
};

export const usePreQuestions = ({ questions: _questions, onSetViewModel }: Props) => {
	const updateQuestions = (questions: ForArrayType<QuestionItemInput[]>["value"], hasError = true) => {
		onSetViewModel([
			{
				key: "questions",
				value: {
					isEdited: true,
					hasError,
					value: questions,
				},
			},
		]);
	};

	const defaultQuestion = {
		questionID: {
			isEdited: false,
			value: getUUID(),
		},
		question: {
			isEdited: false,
			value: "",
		},
		questionType: {
			isEdited: false,
			value: QuestionType.Free,
		},
	} satisfies ForAutoGenerator<QuestionItemInput, "">;

	const appendQuestion = () => {
		const questions = [..._questions.value, defaultQuestion];
		updateQuestions(questions);
	};

	const removeQuestion = (index: number) => () => {
		const questions = _questions.value.filter((_, i) => i !== index);
		updateQuestions(questions);
	};

	const onSetIsRequired = (index: number) => (isRequired: boolean) => {
		const questions = [..._questions.value];
		questions[index].required = {
			isEdited: true,
			value: isRequired,
		};
		updateQuestions(questions);
	};

	const onSetTitle = (index: number) => (input: string) => {
		const questions = [..._questions.value];
		questions[index].question = {
			isEdited: true,
			value: input,
		};
		updateQuestions(questions);
	};

	const onSetQuestionType = (index: number) => (input: QuestionType) => {
		const questions = [..._questions.value];

		if (questions[index].questionType.value !== input) {
			// 質問タイプが変更された場合は、オプションを空にする
			questions[index].options = {
				isEdited: true,
				hasError: true,
				value: [],
			};
		}

		questions[index].questionType = {
			isEdited: true,
			value: input,
		};

		const currentQuestion = questions[index];
		const currentQuestionOptions = currentQuestion.options?.value;
		const shouldAppendOptionItem =
			(!currentQuestionOptions?.length || currentQuestionOptions.length === 0) && // オプションが空の場合
			currentQuestion.questionType.value !== QuestionType.Free; // 質問タイプが自由入力以外の場合

		if (shouldAppendOptionItem) {
			questions[index].options = {
				hasError: true,
				isEdited: false,
				value: [
					{
						isEdited: false,
						value: "",
					},
				],
			};
		}

		updateQuestions(questions);
	};

	const appendOptionItem = (index: number) => () => {
		const defaultValue = {
			isEdited: false,
			value: "",
		} satisfies ForNodeType<string>;

		const questions = [..._questions.value];
		const latestTarget = [...(questions[index].options?.value ?? []), defaultValue];

		questions[index].options = {
			isEdited: true,
			hasError: true,
			value: latestTarget,
		};

		updateQuestions(questions);
	};

	const removeOptionItem = (index: number) => (optionIndex: number) => {
		const questions = [..._questions.value];

		const latestTarget = (questions[index].options?.value || [])?.filter((_, i) => i !== optionIndex);

		if (questions[index].options) {
			questions[index].options = {
				isEdited: true,
				hasError: true,
				value: latestTarget,
			};
		}

		updateQuestions(questions);
	};

	const onSetOptionItem = (index: number) => (optionIndex: number) => (input: string) => {
		const questions = [..._questions.value];
		const latestTarget = [...(questions[index].options?.value ?? [])];
		latestTarget[optionIndex] = {
			isEdited: true,
			value: input,
		};

		questions[index].options = {
			isEdited: true,
			hasError: true,
			value: latestTarget,
		};

		updateQuestions(questions);
	};

	const reorderQuestions = (activeId: string, overId: string) => {
		const list = _questions.value;
		const activeIndex = list.findIndex((q) => q.questionID.value === activeId);
		const overIndex = list.findIndex((q) => q.questionID.value === overId);
		if (activeIndex < 0 || overIndex < 0 || activeIndex === overIndex) {
			return;
		}

		const next = [...list];
		const [removed] = next.splice(activeIndex, 1);
		next.splice(overIndex, 0, removed);

		updateQuestions(next, _questions.hasError);
	};

	return {
		questions: _questions,
		appendQuestion,
		removeQuestion,
		reorderQuestions,
		onSetIsRequired,
		onSetTitle,
		onSetQuestionType,
		appendOptionItem,
		removeOptionItem,
		onSetOptionItem,
	};
};
